import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMapPin, FiUsers, FiCalendar, FiInfo } from 'react-icons/fi';
import { MdCheckCircle } from 'react-icons/md';
import Navbar from '../components/Navbar';
import { matchAPI } from '../services/api';
import { bangladeshCities } from '../utils/bangladeshLocations';

const CreateMatch = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    sport: 'football',
    skillLevel: 'intermediate',
    location: '',
    venue: '',
    date: '',
    time: '',
    duration: '60',
    maxPlayers: '10',
    ageMin: '13',
    ageMax: '65',
    recruitmentClosingDate: '',
    recruitmentClosingTime: '',
    requiresApplication: false,
    applicationQuestions: [''],
    matchImage: '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate required fields
    if (!formData.title || !formData.date || !formData.time || !formData.location || !formData.maxPlayers) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    // If requires application, recruitment closing date is needed
    if (formData.requiresApplication && (!formData.recruitmentClosingDate || !formData.recruitmentClosingTime)) {
      setError('Recruitment closing date and time are required when applications are enabled');
      setLoading(false);
      return;
    }

    try {
      const recruitmentClosingDate = formData.requiresApplication && formData.recruitmentClosingDate
        ? new Date(`${formData.recruitmentClosingDate}T${formData.recruitmentClosingTime}`).toISOString()
        : null;

      const response = await matchAPI.createMatch({
        title: formData.title,
        description: formData.description,
        sport: formData.sport.toLowerCase(),
        skillLevel: formData.skillLevel.toLowerCase(),
        location: formData.location,
        venue: formData.venue,
        date: formData.date,
        time: formData.time,
        duration: parseInt(formData.duration),
        maxPlayers: parseInt(formData.maxPlayers),
        ageRange: {
          min: parseInt(formData.ageMin),
          max: parseInt(formData.ageMax),
        },
        recruitmentClosingDate,
        requiresApplication: formData.requiresApplication,
        applicationQuestions: formData.requiresApplication ? formData.applicationQuestions.filter(q => q.trim() !== '') : [],
        matchImage: formData.matchImage,
      });

      if (response.success) {
        navigate('/dashboard');
      } else {
        setError(response.message || 'Failed to create match');
      }
    } catch (err) {
      setError(err.message || 'Failed to create match. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <div className="bg-dark min-h-screen">
      <Navbar hideAuthButtons={true} />

      <div className="py-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-64px)]">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center space-y-4">
            <p className="text-red-500 text-sm font-semibold uppercase tracking-wider">Organize Your Game</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white">Create a Match</h1>
            <p className="text-gray-400 text-lg">Set up comprehensive match details to attract quality players</p>
          </div>

          {/* Form Container */}
          <div className="card-base p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-10">
              {/* Section 1: Basic Details */}
              <div className="space-y-6 pb-8 border-b border-gray-800">
                <h2 className="text-xl font-bold text-white uppercase tracking-widest flex items-center gap-2">
                  <FiInfo className="text-red-500" />
                  Basic Details
                </h2>

                {/* Match Title */}
                <div>
                  <label htmlFor="title" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                    Match Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. Friday Night 5-a-side Football Match"
                    maxLength="100"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                  />
                </div>

                {/* Sport and Skill Level */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="sport" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      Sport *
                    </label>
                    <select
                      id="sport"
                      name="sport"
                      value={formData.sport}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm appearance-none cursor-pointer"
                    >
                      <option value="football">Football</option>
                      <option value="basketball">Basketball</option>
                      <option value="tennis">Tennis</option>
                      <option value="volleyball">Volleyball</option>
                      <option value="cricket">Cricket</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="skillLevel" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      Skill Level *
                    </label>
                    <select
                      id="skillLevel"
                      name="skillLevel"
                      value={formData.skillLevel}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm appearance-none cursor-pointer"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="professional">Professional</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Add match rules, instructions, special requirements, or any other details..."
                    rows="4"
                    maxLength="500"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
                </div>
              </div>

              {/* Section 2: Location & Venue */}
              <div className="space-y-6 pb-8 border-b border-gray-800">
                <h2 className="text-xl font-bold text-white uppercase tracking-widest flex items-center gap-2">
                  <FiMapPin className="text-red-500" />
                  Location & Venue
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="location" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      City *
                    </label>
                    <select
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm appearance-none cursor-pointer"
                    >
                      <option value="">Select City</option>
                      {bangladeshCities.map((city) => (
                        <option key={city.id} value={city.name}>
                          {city.name} - {city.district}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="venue" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      Specific Venue/Ground Name
                    </label>
                    <input
                      type="text"
                      id="venue"
                      name="venue"
                      value={formData.venue}
                      onChange={handleChange}
                      placeholder="e.g. Central Sports Complex, Green Valley Ground"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Date & Time */}
              <div className="space-y-6 pb-8 border-b border-gray-800">
                <h2 className="text-xl font-bold text-white uppercase tracking-widest flex items-center gap-2">
                  <FiCalendar className="text-red-500" />
                  Date & Time
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="date" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      Match Date *
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="time" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="duration" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                    Match Duration (Minutes) *
                  </label>
                  <select
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm appearance-none cursor-pointer"
                  >
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes (1 hour)</option>
                    <option value="90">90 minutes (1.5 hours)</option>
                    <option value="120">120 minutes (2 hours)</option>
                    <option value="150">150 minutes (2.5 hours)</option>
                    <option value="180">180 minutes (3 hours)</option>
                  </select>
                </div>
              </div>

              {/* Section 4: Players & Age */}
              <div className="space-y-6 pb-8 border-b border-gray-800">
                <h2 className="text-xl font-bold text-white uppercase tracking-widest flex items-center gap-2">
                  <FiUsers className="text-red-500" />
                  Players & Age
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="maxPlayers" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      Maximum Players *
                    </label>
                    <input
                      type="number"
                      id="maxPlayers"
                      name="maxPlayers"
                      value={formData.maxPlayers}
                      onChange={handleChange}
                      min="2"
                      max="50"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                      Age Range (Min - Max)
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        id="ageMin"
                        name="ageMin"
                        value={formData.ageMin}
                        onChange={handleChange}
                        min="13"
                        max="100"
                        placeholder="Min"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                      />
                      <span className="text-gray-500">-</span>
                      <input
                        type="number"
                        id="ageMax"
                        name="ageMax"
                        value={formData.ageMax}
                        onChange={handleChange}
                        min="13"
                        max="100"
                        placeholder="Max"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5: Join Requests */}
              <div className="space-y-6 pb-8 border-b border-gray-800">
                <h2 className="text-xl font-bold text-white uppercase tracking-widest flex items-center gap-2">
                  <MdCheckCircle className="text-red-500" />
                  Join Requests
                </h2>

                <div className="bg-gray-800/50 border border-gray-700 rounded p-4">
                  <label htmlFor="requiresApplication" className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="requiresApplication"
                        name="requiresApplication"
                        checked={formData.requiresApplication}
                        onChange={handleChange}
                        className="w-5 h-5 accent-red-500 cursor-pointer"
                      />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">Enable Join Applications</p>
                      <p className="text-gray-400 text-xs mt-1">
                        Players must submit a request which you review before approving/rejecting them.
                      </p>
                    </div>
                  </label>

                  {formData.requiresApplication && (
                    <div className="mt-6 pt-6 border-t border-gray-700 space-y-6">
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-3">
                          Recruitment Closes When:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="recruitmentClosingDate" className="block text-xs text-gray-500 mb-2">
                              Closing Date *
                            </label>
                            <input
                              type="date"
                              id="recruitmentClosingDate"
                              name="recruitmentClosingDate"
                              value={formData.recruitmentClosingDate}
                              onChange={handleChange}
                              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                            />
                          </div>
                          <div>
                            <label htmlFor="recruitmentClosingTime" className="block text-xs text-gray-500 mb-2">
                              Closing Time *
                            </label>
                            <input
                              type="time"
                              id="recruitmentClosingTime"
                              name="recruitmentClosingTime"
                              value={formData.recruitmentClosingTime}
                              onChange={handleChange}
                              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 text-white rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pt-6 border-t border-gray-700/50">
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-2">
                          Application Questions
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                          Ask players questions when they apply to join (e.g. What position do you play?). Leave blank for no questions.
                        </p>
                        <div className="space-y-3">
                          {formData.applicationQuestions.map((q, index) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="text"
                                value={q}
                                onChange={(e) => {
                                  const newQuestions = [...formData.applicationQuestions];
                                  newQuestions[index] = e.target.value;
                                  setFormData({ ...formData, applicationQuestions: newQuestions });
                                }}
                                placeholder={`Question ${index + 1}`}
                                className="flex-1 px-4 py-2 bg-gray-900 border border-gray-600 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                              />
                              {formData.applicationQuestions.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newQuestions = formData.applicationQuestions.filter((_, i) => i !== index);
                                    setFormData({ ...formData, applicationQuestions: newQuestions });
                                  }}
                                  className="px-3 py-2 bg-red-500/10 text-red-500 hover:text-white rounded hover:bg-red-500 transition"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, applicationQuestions: [...formData.applicationQuestions, ''] });
                          }}
                          className="mt-4 text-sm text-red-500 hover:text-red-400 font-semibold flex items-center gap-1"
                        >
                          <span>+</span> Add Another Question
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {!formData.requiresApplication && (
                  <p className="text-xs text-gray-500 text-center py-2">
                    Players can join freely without needing approval
                  </p>
                )}
              </div>

              {/* Section 6: Cover Image */}
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-white uppercase tracking-widest">Match Cover Image</h2>

                <div>
                  <label htmlFor="matchImage" className="block text-xs font-bold text-gray-400 uppercase tracking-[0.2em] mb-3">
                    Cover Image URL
                  </label>
                  <input
                    type="url"
                    id="matchImage"
                    name="matchImage"
                    value={formData.matchImage}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">A cover image makes your match more attractive to players</p>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-4">
                  <p className="text-red-400 text-sm font-semibold">{error}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-4 pt-8 border-t border-gray-800">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 border-2 border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 font-semibold py-3 px-4 rounded transition-all duration-300 text-sm uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-500 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)] disabled:opacity-50 text-white font-semibold py-3 px-4 rounded transition-all duration-300 text-sm uppercase tracking-wider"
                >
                  {loading ? 'Creating Match...' : 'Create Match'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMatch;
